import React from 'react';

interface PassFailData {
    examPerformance?: {
        passCount: number;
        failCount: number;
        passRate: number;
        failRate: number;
        totalExams: number;
        pendingResults: number;
    };
}

interface PassFailChartProps {
    data: PassFailData;
    title?: string;
}

export const PassFailChart: React.FC<PassFailChartProps> = ({ data, title = "Pass/Fail Analysis" }) => {
    if (!data?.examPerformance) {
        return (
            <div className="bg-white rounded-lg shadow p-6">
                <h3 className="text-lg font-semibold mb-4">{title}</h3>
                <div className="text-center py-8">
                    <div className="text-4xl mb-2">📊</div>
                    <p className="text-gray-500">No exam data available</p>
                </div>
            </div>
        );
    }

    const { passCount, failCount, passRate, failRate, totalExams, pendingResults } = data.examPerformance;
    const completedExams = passCount + failCount;

    return (
        <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-semibold mb-4">{title}</h3>

            {/* Summary Stats */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div className="text-center p-3 bg-green-50 rounded-lg">
                    <div className="text-2xl font-bold text-green-600">{passCount}</div>
                    <div className="text-sm text-green-700">Passed</div>
                </div>
                <div className="text-center p-3 bg-red-50 rounded-lg">
                    <div className="text-2xl font-bold text-red-600">{failCount}</div>
                    <div className="text-sm text-red-700">Failed</div>
                </div>
                <div className="text-center p-3 bg-yellow-50 rounded-lg">
                    <div className="text-2xl font-bold text-yellow-600">{pendingResults}</div>
                    <div className="text-sm text-yellow-700">Pending</div>
                </div>
                <div className="text-center p-3 bg-blue-50 rounded-lg">
                    <div className="text-2xl font-bold text-blue-600">{totalExams}</div>
                    <div className="text-sm text-blue-700">Total</div>
                </div>
            </div>

            {/* Visual Bar Chart */}
            {completedExams > 0 && (
                <div className="mb-6">
                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Pass Rate</span>
                        <span className="text-sm font-medium text-gray-900">{passRate.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
                        <div
                            className="bg-green-500 h-4 rounded-full transition-all duration-500"
                            style={{ width: `${passRate}%` }}
                        ></div>
                    </div>

                    <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Fail Rate</span>
                        <span className="text-sm font-medium text-gray-900">{failRate.toFixed(1)}%</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-4">
                        <div
                            className="bg-red-500 h-4 rounded-full transition-all duration-500"
                            style={{ width: `${failRate}%` }}
                        ></div>
                    </div>
                </div>
            )}

            {/* Detailed Breakdown */}
            <div className="space-y-3">
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Completed Exams</span>
                    <span className="font-medium">{completedExams}</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Success Rate</span>
                    <span className="font-medium text-green-600">{passRate.toFixed(1)}%</span>
                </div>
                <div className="flex justify-between items-center py-2 border-b border-gray-100">
                    <span className="text-gray-600">Failure Rate</span>
                    <span className="font-medium text-red-600">{failRate.toFixed(1)}%</span>
                </div>
                {pendingResults > 0 && (
                    <div className="flex justify-between items-center py-2">
                        <span className="text-gray-600">Awaiting Results</span>
                        <span className="font-medium text-yellow-600">{pendingResults}</span>
                    </div>
                )}
            </div>

            {/* Performance Indicator */}
            <div className="mt-6 p-4 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50">
                <div className="flex items-center justify-between">
                    <div>
                        <div className="text-sm font-medium text-gray-700">Overall Performance</div>
                        <div className="text-xs text-gray-500">Based on completed exams</div>
                    </div>
                    <div className="text-right">
                        <div className={`text-2xl font-bold ${passRate >= 80 ? 'text-green-600' : passRate >= 60 ? 'text-yellow-600' : 'text-red-600'}`}>
                            {passRate >= 80 ? '🎯' : passRate >= 60 ? '⚠️' : '📉'}
                        </div>
                        <div className="text-xs text-gray-500">
                            {passRate >= 80 ? 'Excellent' : passRate >= 60 ? 'Good' : 'Needs Improvement'}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};